/**
 * Search component.
 * @module components/theme/Search/Search
 */

import React, { useState, Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { asyncConnect } from '@plone/volto/helpers/AsyncConnect';
import { FormattedMessage, FormattedPlural } from 'react-intl';
import { createPortal } from 'react-dom';
import {
  Container,
  Pagination,
  Button,
  ButtonContent,
  Header,
  Dropdown,
  Checkbox,
  Divider,
  Transition,
} from 'semantic-ui-react';
import qs from 'query-string';
import classNames from 'classnames';
import { defineMessages, injectIntl } from 'react-intl';
import config from '@plone/volto/registry';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { searchContent } from '@plone/volto/actions/search/search';
import SearchTags from '@plone/volto/components/theme/Search/SearchTags';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import cx from 'classnames';
import paginationLeftSVG from '@plone/volto/icons/left-key.svg';
import paginationRightSVG from '@plone/volto/icons/right-key.svg';
import SummaryTemplate from '../../../../../components/Blocks/Listings/SummaryTemplate';
import filter from '@plone/volto/icons/filter.svg';
import settings from '@plone/volto/icons/globe.svg';
import messages from '../../../../../messages';

const globalFacets = [
  'portal_type',
  'taxonomy_person_type',
  'taxonomy_project_type',
];

const globalSearchOptions = (options) => {
  options['use_site_search_settings'] = 1;
  options['facets'] = [];
  globalFacets?.forEach((facet) => {
    options['facets'].push(facet);
  });
  return options;
};

const ItemBlockListing = ({ items }) => {
  return items.map((item) => {
    const CustomItemBodyTemplate = config.getComponent({
      name: 'SummaryListingItemTemplate',
      dependencies: [item['@type']],
    }).component;
    if (!CustomItemBodyTemplate) {
      return (
        <article className="tileItem" key={item['@id']}>
          <h2 className="tileHeadline">
            <UniversalLink
              item={item}
              className="summary url"
              title={item['@type']}
            >
              {item.title}
            </UniversalLink>
          </h2>
          {item.description && (
            <div className="tileBody">
              <span className="description">{item.description}</span>
            </div>
          )}
          <div className="tileFooter">
            <UniversalLink item={item}>
              <FormattedMessage id="Read More…" defaultMessage="Read More…" />
            </UniversalLink>
          </div>
          <div className="visualClear" />
        </article>
      );
    } else {
      return (
        <div
          className={cx(
            'listing-item q container',
            item['@type'].replace('saw.persons.', ''),
          )}
          key={item['@id']}
        >
          <CustomItemBodyTemplate
            item={item}
            isEditMode={false}
            key={item['@id']}
          />
        </div>
      );
    }
  });
};

const ShortSummaryTemplate = ({ items }) => {
  return (
    <div className="items">
      {items.map((item) => {
        return (
          <article className="tileItem" key={item['@id']}>
            <h2 className="tileHeadline">
              <UniversalLink
                item={item}
                className="summary url"
                title={item['@type']}
              >
                {item.title}
              </UniversalLink>
            </h2>
            {item.description && (
              <div className="tileBody">
                <span className="description">{item.description}</span>
              </div>
            )}

            <div className="visualClear" />
          </article>
        );
      })}
    </div>
  );
};

const ItemListing = ({ items, short = true }) => {
  if (short) {
    return <ShortSummaryTemplate items={items} />;
  }
  return <SummaryTemplate items={items} />;
};

const FilterMenu = (props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleVisibility = () => setMenuOpen(!menuOpen);

  return (
    <div>
      <Button content={menuOpen ? 'Hide' : 'Show'} onClick={toggleVisibility} />
      <Divider hidden />
      <Transition visible={menuOpen} animation="scale" duration={500}>
        <div className="result-settings">
          <div className="header-content">
            <div className="sort-by">
              <FormattedMessage id="Filter By:" defaultMessage="Filter By:" />
            </div>
            {Object.keys(props.facets || {}).map((facet) => {
              if (props.facets?.[facet]?.items.length === 0) {
                return null;
              }
              const value =
                (props.query?.[facet] || []) instanceof Array
                  ? props.query?.[facet] || []
                  : [props.query?.[facet]];
              return (
                <Dropdown
                  key={facet}
                  // onChange={this.onFilterChange}
                  // onClose={this.onFilterClose}
                  value={value}
                  name={facet}
                  placeholder={
                    facet in messages
                      ? props.intl.formatMessage(messages[facet])
                      : facet
                  }
                  fluid
                  multiple
                  selection
                  options={
                    props.facets?.[facet]?.items.map((item) => ({
                      key: item.value,
                      text: `${item.title} (${item.total})`,
                      value: item.value,
                    })) || []
                  }
                ></Dropdown>
              );
            })}
          </div>
          <div className="header-content result-view">
            <Checkbox
              slider
              // onChange={this.onGroupingChange}
              checked={this.state.grouping}
              label={props.intl.formatMessage(messages.groupResults)}
            />
            <Checkbox
              slider
              // onChange={this.onListingChange}
              checked={this.state.shortListing}
              label={props.intl.formatMessage(messages.shortListing)}
            />
          </div>
          <div className="header-content">
            <div className="sort-by">
              <FormattedMessage id="Sort By:" defaultMessage="Sort by:" />
            </div>
            <Button
              onClick={(event) => {
                this.onSortChange(event);
              }}
              name="relevance"
              size="tiny"
              className={classNames('button-sort', {
                'button-active': this.state.sortBy === 'relevance',
              })}
            >
              <FormattedMessage id="Relevance" defaultMessage="Relevance" />
            </Button>
            <Button
              onClick={(event) => {
                this.onSortChange(event);
              }}
              name="sortable_portal_type"
              size="tiny"
              className={classNames('button-sort', {
                'button-active': this.state.sortBy === 'sortable_portal_type',
              })}
            >
              <FormattedMessage id="portal-type" defaultMessage="Inhaltstyp" />
            </Button>
            <Button
              onClick={(event) => {
                this.onSortChange(event);
              }}
              name="sortable_title"
              size="tiny"
              className={classNames('button-sort', {
                'button-active': this.state.sortBy === 'sortable_title',
              })}
            >
              <FormattedMessage
                id="Alphabetically"
                defaultMessage="Alphabetically"
              />
            </Button>
            <Button
              onClick={(event) => {
                this.onSortChange(event, 'reverse');
              }}
              name="effective"
              size="tiny"
              className={classNames('button-sort', {
                'button-active': this.state.sortBy === 'effective',
              })}
            >
              <FormattedMessage
                id="Date (newest first)"
                defaultMessage="Date (newest first)"
              />
            </Button>
          </div>
        </div>
      </Transition>
    </div>
  );
};

/**
 * Search class.
 * @class SearchComponent
 * @extends Component
 */
class Search extends Component {
  /**
   * Property types.
   * @property {Object} propTypes Property types.
   * @static
   */
  static propTypes = {
    searchContent: PropTypes.func.isRequired,
    searchableText: PropTypes.string,
    subject: PropTypes.string,
    path: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
      }),
    ),
    pathname: PropTypes.string.isRequired,
  };

  /**
   * Default properties.
   * @property {Object} defaultProps Default properties.
   * @static
   */
  static defaultProps = {
    items: [],
    facets: {},
    searchableText: null,
    subject: null,
    path: null,
  };

  constructor(props) {
    super(props);
    this.defaultPageSize = config.settings.defaultPageSize;
    this.defaultPageSize = 1000;
    this.state = {
      currentPage: 1,
      isClient: false,
      // sortBy: 'relevance',
      menuOpen: false,
      sortBy: 'sortable_portal_type',
      grouping: true,
      shortListing: true,
      filterBy: {},
    };
  }

  /**
   * Component did mount
   * @method componentDidMount
   * @returns {undefined}
   */
  componentDidMount() {
    this.doSearch();
    this.setState({ isClient: true });
  }

  /**
   * Component will receive props
   * @method componentWillReceiveProps
   * @param {Object} nextProps Next properties
   * @returns {undefined}
   */
  UNSAFE_componentWillReceiveProps = (nextProps) => {
    if (this.props.location.search !== nextProps.location.search) {
      this.doSearch();
    }
  };

  /**
   * Search based on the given searchableText, subject and path.
   * @method doSearch
   * @param {string} searchableText The searchable text string
   * @param {string} subject The subject (tag)
   * @param {string} path The path to restrict the search to
   * @returns {undefined}
   */

  doSearch = () => {
    const options = qs.parse(this.props.history.location.search);
    const searchroot = this.props.pathname.replace(/\/search$/, '');
    this.setState({ currentPage: 1 });
    globalSearchOptions(options);
    options.sort_on =
      this.state.sortBy === 'relevance' ? undefined : this.state.sortBy;
    this.props.searchContent(searchroot, {
      b_size: this.defaultPageSize,
      ...options,
    });
  };

  // addSearchOptions = (options) => {
  //   options['use_site_search_settings'] = 1;
  //   options['facets'] = [];
  //   this.defaultFacets?.forEach((facet) => {
  //     options['facets'].push(facet);
  //   });
  //   return options;
  // };

  handleQueryPaginationChange = (e, { activePage }) => {
    window.scrollTo(0, 0);
    let options = qs.parse(this.props.history.location.search);
    globalSearchOptions(options);
    const searchroot = this.props.pathname.replace(/\/search$/, '');
    this.setState({ currentPage: activePage }, () => {
      this.props.searchContent(searchroot, {
        b_size: this.defaultPageSize,
        ...options,
        b_start:
          (this.state.currentPage - 1) *
          (options.b_size || this.defaultPageSize),
      });
    });
  };

  onSortChange = (event, sort_order) => {
    let options = qs.parse(this.props.history.location.search);
    globalSearchOptions(options);
    options.sort_on = event.target.name;
    options.sort_order = sort_order || 'ascending';
    if (event.target.name === 'relevance') {
      delete options.sort_on;
      delete options.sort_order;
    }
    let searchParams = qs.stringify(options);
    this.setState({ currentPage: 1, sortBy: event.target.name }, () => {
      // eslint-disable-next-line no-restricted-globals
      this.props.history.replace({
        search: searchParams,
      });
    });
  };

  onFilterChange = (event, data) => {
    // console.log('change', event, data);
    let options = qs.parse(this.props.history.location.search);
    globalSearchOptions(options);
    options = {
      ...options,
      [data.name]: data.value,
    };
    let searchParams = qs.stringify(options);
    this.setState({ currentPage: 1, filterBy: {} }, () => {
      // eslint-disable-next-line no-restricted-globals
      this.props.history.replace({
        search: searchParams,
      });
    });
    return;
  };

  onFilterChangeState = (event, data) => {
    // console.log('change', event, data);
    this.setState({
      filterBy: {
        ...this.state.filterBy,
        [data.name]: [...(this.state.filterBy[data.name] || []), ...data.value],
      },
    });
    return;
  };

  onFilterClose = (event, data) => {
    // console.log('close', event, data);
    let options = qs.parse(this.props.history.location.search);
    globalSearchOptions(options);
    options = {
      ...options,
      ...this.state.filterBy,
    };
    let searchParams = qs.stringify(options);
    this.setState({ currentPage: 1, filterBy: {} }, () => {
      // eslint-disable-next-line no-restricted-globals
      this.props.history.replace({
        search: searchParams,
      });
    });
  };

  onGroupingChange = () => {
    this.setState({ grouping: !this.state.grouping });
  };

  toggleVisibility = () => {
    this.setState({ menuOpen: !this.state.menuOpen });
  };

  onListingChange = () => {
    this.setState({ shortListing: !this.state.shortListing });
  };

  getCountForGroup = (facet, group) => {
    return (
      this.props.facets?.[facet]?.items.find((item) => item.value === group)
        ?.total || 0
    );
  };

  /**
   * Render method.
   * @method render
   * @returns {string} Markup for the component.
   */
  render() {
    const options = qs.parse(this.props.history.location.search);

    const groupedItems = this.state.grouping
      ? this.props.items.reduce((x, item) => {
          (x[item['@type']] = x[item['@type']] || []).push(item);

          return x;
        }, {})
      : { 'no-grouping': this.props.items };
    return (
      <>
        <Helmet title={this.props.intl.formatMessage(messages.Search)} />
        <Container id="page-search">
          <div className="container">
            <article id="content">
              <header>
                <h1 className="documentFirstHeading">
                  {this.props.searchableText ? (
                    <FormattedMessage
                      id="Search results for {term}"
                      defaultMessage="Search results for {term}"
                      values={{
                        term: <q>{this.props.searchableText}</q>,
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      id="Search results"
                      defaultMessage="Search results"
                    />
                  )}
                </h1>

                {this.props.search?.items_total > 0 ? (
                  <div className="items_total">
                    <div className="results-count">
                      {this.props.search.items_total}{' '}
                      <FormattedMessage
                        id="results found"
                        defaultMessage="results"
                      />{' '}
                      <Button circular icon onClick={this.toggleVisibility}>
                        <Icon size="20px" name={filter} />
                        {/* {this.state.menuOpen ? (
                          <>
                            <ButtonContent hidden>Hide</ButtonContent>
                            <ButtonContent visible>
                              <Icon name={filter} />
                            </ButtonContent>
                          </>
                        ) : (
                          <>
                            <ButtonContent hidden>Show</ButtonContent>
                            <ButtonContent visible>
                              <Icon name={filter} />
                            </ButtonContent>
                          </>
                        )} */}
                      </Button>
                    </div>

                    <div className="filter-pane">
                      <Transition
                        visible={this.state.menuOpen}
                        animation="scale"
                        duration={500}
                      >
                        <div className="transition-wrapper">
                          <div className="result-settings">
                            <div className="header-content result-filter">
                              <div className="sort-by">
                                <FormattedMessage
                                  id="Filter By:"
                                  defaultMessage="Filter By:"
                                />
                              </div>
                              {Object.keys(this.props.facets || {}).map(
                                (facet) => {
                                  if (
                                    this.props.facets?.[facet]?.items.length ===
                                    0
                                  ) {
                                    return null;
                                  }
                                  const value =
                                    (this.props.query?.[facet] || []) instanceof
                                    Array
                                      ? this.props.query?.[facet] || []
                                      : [this.props.query?.[facet]];
                                  return (
                                    <Dropdown
                                      key={facet}
                                      onChange={this.onFilterChange}
                                      // onClose={this.onFilterClose}
                                      value={value}
                                      name={facet}
                                      placeholder={
                                        facet in messages
                                          ? this.props.intl.formatMessage(
                                              messages[facet],
                                            )
                                          : facet
                                      }
                                      fluid
                                      multiple
                                      selection
                                      options={
                                        this.props.facets?.[facet]?.items.map(
                                          (item) => ({
                                            key: item.value,
                                            text: `${item.title} (${item.total})`,
                                            value: item.value,
                                          }),
                                        ) || []
                                      }
                                    ></Dropdown>
                                  );
                                },
                              )}
                            </div>
                            <div className="header-content result-view">
                              <div className="sort-by">
                                <FormattedMessage
                                  id="ViewSettings"
                                  defaultMessage="view settings"
                                />
                              </div>
                              <Checkbox
                                slider
                                onChange={this.onGroupingChange}
                                checked={this.state.grouping}
                                label={this.props.intl.formatMessage(
                                  messages.groupResults,
                                )}
                              />
                              <Checkbox
                                slider
                                onChange={this.onListingChange}
                                checked={this.state.shortListing}
                                label={this.props.intl.formatMessage(
                                  messages.shortListing,
                                )}
                              />
                            </div>
                            <div className="header-content result-sort">
                              <div className="sort-by">
                                <FormattedMessage
                                  id="Sort By:"
                                  defaultMessage="Sort by:"
                                />
                              </div>
                              <Button
                                onClick={(event) => {
                                  this.onSortChange(event);
                                }}
                                name="relevance"
                                size="tiny"
                                className={classNames('button-sort', {
                                  'button-active':
                                    this.state.sortBy === 'relevance',
                                })}
                              >
                                <FormattedMessage
                                  id="Relevance"
                                  defaultMessage="Relevance"
                                />
                              </Button>
                              <Button
                                onClick={(event) => {
                                  this.onSortChange(event);
                                }}
                                name="sortable_portal_type"
                                size="tiny"
                                className={classNames('button-sort', {
                                  'button-active':
                                    this.state.sortBy ===
                                    'sortable_portal_type',
                                })}
                              >
                                <FormattedMessage
                                  id="portal-type"
                                  defaultMessage="Inhaltstyp"
                                />
                              </Button>
                              <Button
                                onClick={(event) => {
                                  this.onSortChange(event);
                                }}
                                name="sortable_title"
                                size="tiny"
                                className={classNames('button-sort', {
                                  'button-active':
                                    this.state.sortBy === 'sortable_title',
                                })}
                              >
                                <FormattedMessage
                                  id="Alphabetically"
                                  defaultMessage="Alphabetically"
                                />
                              </Button>
                              <Button
                                onClick={(event) => {
                                  this.onSortChange(event, 'reverse');
                                }}
                                name="effective"
                                size="tiny"
                                className={classNames('button-sort', {
                                  'button-active':
                                    this.state.sortBy === 'effective',
                                })}
                              >
                                <FormattedMessage
                                  id="Date (newest first)"
                                  defaultMessage="Date (newest first)"
                                />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Transition>
                    </div>
                  </div>
                ) : (
                  <div>
                    <FormattedMessage
                      id="No results found"
                      defaultMessage="No results found"
                    />
                  </div>
                )}
              </header>
              <section id="content-core">
                {/* {Object.keys(this.props.facets || {}).map((facet) => (
                <div key={facet} className="search-group">
                  <h2 className="search-group-title">{facet}</h2>
                  {this.props.facets[facet].items.map((item) => (
                    <div key={item.name} className="search-facet-item">
                      <UniversalLink
                        href={`${this.props.pathname}?${qs.stringify({
                          ...options,
                          [facet]: item.value,
                        })}`}
                      >
                        {item.title} ({item.total})
                      </UniversalLink>
                    </div>
                  ))}
                </div>
              ))} */}
                {Object.keys(groupedItems).map((group) => (
                  <div key={group} className="search-group">
                    {group !== 'no-grouping' && (
                      <h2 className="search-group-title">
                        {group in messages
                          ? this.props.intl.formatMessage(messages[group], {
                              count: this.getCountForGroup(
                                'portal_type',
                                group,
                              ),
                            })
                          : group}
                      </h2>
                    )}
                    <div className="block listing summary">
                      <ItemListing
                        key={group}
                        items={groupedItems[group]}
                        short={this.state.shortListing}
                      />
                    </div>
                  </div>
                ))}

                {this.props.search?.batching && (
                  <div className="search-footer">
                    <Pagination
                      activePage={this.state.currentPage}
                      totalPages={Math.ceil(
                        this.props.search.items_total /
                          (options.b_size || this.defaultPageSize),
                      )}
                      onPageChange={this.handleQueryPaginationChange}
                      firstItem={null}
                      lastItem={null}
                      prevItem={{
                        content: <Icon name={paginationLeftSVG} size="18px" />,
                        icon: true,
                        'aria-disabled': !this.props.search.batching.prev,
                        className: !this.props.search.batching.prev
                          ? 'disabled'
                          : null,
                      }}
                      nextItem={{
                        content: <Icon name={paginationRightSVG} size="18px" />,
                        icon: true,
                        'aria-disabled': !this.props.search.batching.next,
                        className: !this.props.search.batching.next
                          ? 'disabled'
                          : null,
                      }}
                    />
                  </div>
                )}
              </section>
            </article>
          </div>
          {this.state.isClient &&
            createPortal(
              <Toolbar
                pathname={this.props.pathname}
                hideDefaultViewButtons
                inner={<span />}
              />,
              document.getElementById('toolbar'),
            )}
        </Container>
      </>
    );
  }
}

export const __test__ = compose(
  injectIntl,
  connect(
    (state, props) => ({
      items: state.search.items,
      facets: state.search.facets,
      searchableText: qs.parse(props.history.location.search).SearchableText,
      query: qs.parse(props.history.location.search),
      pathname: props.history.location.pathname,
    }),
    { searchContent },
  ),
)(Search);

export default compose(
  injectIntl,
  connect(
    (state, props) => ({
      items: state.search.items,
      facets: state.search.facets,
      searchableText: qs.parse(props.history.location.search).SearchableText,
      query: qs.parse(props.history.location.search),
      pathname: props.location.pathname,
    }),
    { searchContent },
  ),
  asyncConnect([
    {
      key: 'search',
      promise: ({ location, store: { dispatch } }) =>
        // const searchroot = location.pathname.replace(/\/search$/, '');
        dispatch(
          searchContent(
            location.pathname.replace(/\/search$/, ''),
            globalSearchOptions({
              ...qs.parse(location.search),
              use_site_search_settings: 1,
            }),
          ),
        ),
    },
  ]),
)(Search);
